import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';

const NAV_TABS = [
    {
        to: '/dashboard',
        icon: '🏠',
        labelKey: 'home',
        color: '#3b82f6',
        gradient: 'linear-gradient(135deg, #3b82f6, #2563eb)',
        glow: 'rgba(59,130,246,0.35)',
    },
    // Tạm ẩn — bỏ comment để hiện lại
    // {
    //     to: '/learning-corner',
    //     icon: '📚',
    //     labelKey: 'learningCorner',
    //     color: '#f59e0b',
    //     gradient: 'linear-gradient(135deg, #f59e0b, #d97706)',
    //     glow: 'rgba(245,158,11,0.35)',
    // },
    {
        to: '/skill-practice',
        icon: '✏️',
        labelKey: 'skillPractice',
        color: '#8b5cf6',
        gradient: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
        glow: 'rgba(139,92,246,0.35)',
    },
    {
        to: '/library',
        icon: '📚',
        labelKey: 'library',
        color: '#2A6B30',
        gradient: 'linear-gradient(135deg, #2A6B30, #1E5C2E)',
        glow: 'rgba(42,107,48,0.35)',
    },
    {
        to: '/full-mock-test',
        icon: '📝',
        labelKey: 'fullMockTests',
        color: '#10b981',
        gradient: 'linear-gradient(135deg, #10b981, #059669)',
        glow: 'rgba(16,185,129,0.35)',
    },
    {
        to: '/recent-activity',
        icon: '📈',
        labelKey: 'recentActivity',
        color: '#06b6d4',
        gradient: 'linear-gradient(135deg,#06b6d4,#0891b2)',
        glow: 'rgba(6,182,212,0.35)',
    },
    {
        to: '/settings',
        icon: '⚙️',
        labelKey: 'settings',
        color: '#64748b',
        gradient: 'linear-gradient(135deg, #64748b, #475569)',
        glow: 'rgba(100,116,139,0.35)',
    },
];

const injectStyles = () => {
    if (document.getElementById('bottom-tab-styles')) return;
    const style = document.createElement('style');
    style.id = 'bottom-tab-styles';
    style.textContent = `
    @keyframes tabIconBounce {
      0%   { transform: scale(1) translateY(0); }
      35%  { transform: scale(1.25) translateY(-5px); }
      65%  { transform: scale(1.05) translateY(-2px); }
      100% { transform: scale(1) translateY(0); }
    }
    @keyframes tabBarSlideUp {
      from { transform: translateY(100%); opacity: 0; }
      to   { transform: translateY(0);    opacity: 1; }
    }
    .bottom-tab-bar { animation: tabBarSlideUp 0.45s cubic-bezier(0.34,1.56,0.64,1) both; }
    .tab-btn {
      flex: 1;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 5px;
      background: transparent;
      border: none;
      cursor: pointer;
      padding: 8px 4px 6px;
      position: relative;
      transition: opacity 0.2s;
      min-width: 0;
    }
    .tab-btn:hover .tab-icon-box { transform: scale(1.08); }
    .tab-btn.active .tab-icon-box {
      animation: tabIconBounce 0.5s cubic-bezier(0.34,1.56,0.64,1) both;
    }
    .tab-icon-box {
      width: 44px; height: 44px;
      border-radius: 14px;
      display: flex; align-items: center; justify-content: center;
      transition: transform 0.2s, box-shadow 0.2s;
      flex-shrink: 0;
    }
    .tab-label {
      font-size: 10px; font-weight: 500; color: #94a3b8;
      text-align: center; line-height: 1.3;
      word-break: break-word; white-space: normal;
      width: 100%; transition: color 0.2s, font-weight 0.2s;
    }
    .tab-btn.active .tab-label { font-weight: 800; font-size: 10px; }
  `;
    document.head.appendChild(style);
};

const BottomTabBar = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { t, lang } = useLanguage();

    useEffect(() => { injectStyles(); }, []);

    // "settings" key might not exist in translations — fallback
    const getLabel = (tab) => {
        if (tab.labelKey === 'settings') return lang === 'vi' ? 'Cài Đặt' : 'Settings';
        if (tab.labelKey === 'library') return lang === 'vi' ? 'Thư Viện' : 'Library';
        return t(tab.labelKey);
    };

    const handleTabClick = (tab) => {
        if (!location.pathname.startsWith(tab.to)) navigate(tab.to);
    };

    return (
        <nav
            className="bottom-tab-bar"
            style={{
                position: 'fixed',
                bottom: 0, left: 0, right: 0,
                zIndex: 1000,
                display: 'flex',
                alignItems: 'stretch',
                background: 'rgba(255,255,255,0.94)',
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                borderTop: '1px solid rgba(0,0,0,0.07)',
                boxShadow: '0 -6px 30px rgba(0,0,0,0.09)',
                height: 88,
                paddingBottom: 'env(safe-area-inset-bottom, 0px)',
            }}
        >
            {/* Rainbow top stripe */}
            <div style={{
                position: 'absolute', top: 0, left: 0, right: 0, height: 3,
                background: 'linear-gradient(90deg, #3b82f6 0%, #8b5cf6 20%, #6366f1 40%, #10b981 60%, #f59e0b 78%, #06b6d4 90%, #64748b 100%)',
            }} />

            {NAV_TABS.map((tab) => {
                const isActive =
                    location.pathname === tab.to ||
                    location.pathname.startsWith(tab.to + '/');

                return (
                    <button
                        key={tab.to}
                        className={`tab-btn${isActive ? ' active' : ''}`}
                        onClick={() => handleTabClick(tab)}
                        title={getLabel(tab)}
                    >
                        {/* Icon box */}
                        <div
                            className="tab-icon-box"
                            style={isActive ? {
                                background: tab.gradient,
                                boxShadow: `0 6px 18px ${tab.glow}`,
                            } : { background: 'rgba(0,0,0,0.04)' }}
                        >
                            <span style={{
                                fontSize: 22, lineHeight: 1,
                                filter: isActive ? 'brightness(0) invert(1)' : 'none',
                            }}>
                                {tab.icon}
                            </span>
                        </div>

                        {/* Label */}
                        <span
                            className="tab-label"
                            style={{ color: isActive ? tab.color : '#94a3b8' }}
                        >
                            {getLabel(tab)}
                        </span>

                        {/* Active dot */}
                        {isActive && (
                            <div style={{
                                width: 5, height: 5, borderRadius: '50%',
                                background: tab.color,
                                boxShadow: `0 0 7px ${tab.glow}`,
                                marginTop: -2,
                            }} />
                        )}
                    </button>
                );
            })}
        </nav>
    );
};

export default BottomTabBar;
